/**
 * Exact service → execution binding registry.
 *
 * Contract: docs/source-of-truth/worker-service-execution-matrix.md
 * ("Exact catalogue coverage" and "Matrix publication gates").
 *
 * Every one of the 49 official Support at Home services binds to exactly one
 * base pack and at least one allowed execution mode. There is deliberately no
 * default, no wildcard and no generic fallback: `bindingForServiceCode` returns
 * `null` for an unknown code and callers must fail closed. An unmapped code is
 * a coordinator configuration error, not something to guess at the point of
 * care.
 *
 * Ordering note: the first entry of `allowedModes` is the default mode used
 * when an inclusion does not name one.
 */

import { OFFICIAL_SAH_SERVICE_CATALOGUE } from '../sah-service-list';
import type { OfficialSahUnitType } from '../sah-service-list';
import type { ExecutionMode } from './execution-modes';
import { isModeCompatibleWithUnitType } from './execution-modes';
import type { ServicePackCode } from './packs';

export interface ServiceExecutionBinding {
  serviceCode: string;
  allowedModes: readonly ExecutionMode[];
  basePack: ServicePackCode;
}

function bind(
  serviceCode: string,
  basePack: ServicePackCode,
  ...allowedModes: ExecutionMode[]
): ServiceExecutionBinding {
  return { serviceCode, basePack, allowedModes };
}

/**
 * Transcribed from the execution matrix. Row order follows the matrix so the
 * two can be diffed by eye.
 */
export const SERVICE_EXECUTION_BINDINGS: readonly ServiceExecutionBinding[] = [
  // --- Nursing care -------------------------------------------------------
  bind(
    'SAH_CLINICAL_NURSING_REGISTERED_NURSE',
    'NURSING_CLINICAL',
    'INDIVIDUAL_VISIT',
    'MULTI_WORKER_VISIT',
  ),
  bind(
    'SAH_CLINICAL_NURSING_ENROLLED_NURSE',
    'NURSING_CLINICAL',
    'INDIVIDUAL_VISIT',
    'MULTI_WORKER_VISIT',
  ),
  // Delegated support, never inherited RN authority.
  bind(
    'SAH_CLINICAL_NURSING_ASSISTANT',
    'DELEGATED_CLINICAL_SUPPORT',
    'INDIVIDUAL_VISIT',
    'MULTI_WORKER_VISIT',
  ),
  // An item, not a nursing visit.
  bind('SAH_CLINICAL_NURSING_CONSUMABLES', 'CLINICAL_ITEM', 'FULFILMENT'),

  // --- Allied health and other therapeutic services -----------------------
  bind(
    'SAH_CLINICAL_ALLIED_ATSI_HEALTH_PRACTITIONER',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_ATSI_HEALTH_WORKER',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_THERAPY_ASSISTANT',
    'ALLIED_ASSISTANT',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_COUNSELLOR_PSYCHOTHERAPIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_DIETITIAN_NUTRITIONIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_EXERCISE_PHYSIOLOGIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_MUSIC_THERAPIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_OCCUPATIONAL_THERAPIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_PHYSIOTHERAPIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind('SAH_CLINICAL_ALLIED_PODIATRIST', 'ALLIED_PROFESSIONAL', 'INDIVIDUAL_VISIT'),
  bind(
    'SAH_CLINICAL_ALLIED_PSYCHOLOGIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_SOCIAL_WORKER',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_CLINICAL_ALLIED_SPEECH_PATHOLOGIST',
    'ALLIED_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),

  // --- Prescribed nutrition -----------------------------------------------
  bind('SAH_CLINICAL_NUTRITION_PRESCRIBED', 'NUTRITION_ITEM', 'FULFILMENT'),

  // --- Care management (non-visit, billed per bill) -----------------------
  bind('SAH_CARE_MANAGEMENT_HOME_SUPPORT', 'CARE_MANAGEMENT', 'CASE_ACTIVITY'),
  bind('SAH_RESTORATIVE_CARE_MANAGEMENT_HOME_SUPPORT', 'CARE_MANAGEMENT', 'CASE_ACTIVITY'),

  // --- Personal care ------------------------------------------------------
  bind(
    'SAH_INDEPENDENCE_PERSONAL_CARE_SELF_CARE_ADL',
    'PERSONAL_CARE_ADL',
    'INDIVIDUAL_VISIT',
    'MULTI_WORKER_VISIT',
  ),
  // Assistance with self-administration only. Administering is a different
  // authority and a different service.
  bind(
    'SAH_INDEPENDENCE_PERSONAL_CARE_SELF_ADMIN_MEDICATION',
    'MEDICATION_SELF_ADMIN_ASSIST',
    'INDIVIDUAL_VISIT',
  ),
  bind(
    'SAH_INDEPENDENCE_PERSONAL_CARE_CONTINENCE_NON_CLINICAL',
    'CONTINENCE_NONCLINICAL',
    'INDIVIDUAL_VISIT',
    'MULTI_WORKER_VISIT',
  ),

  // --- Social support and community engagement ----------------------------
  // Group only: one session with participant children, never cloned shifts.
  bind('SAH_INDEPENDENCE_SOCIAL_GROUP', 'SOCIAL_GROUP', 'GROUP_SESSION'),
  bind('SAH_INDEPENDENCE_SOCIAL_INDIVIDUAL', 'SOCIAL_INDIVIDUAL', 'INDIVIDUAL_VISIT'),
  bind('SAH_INDEPENDENCE_SOCIAL_ACCOMPANIED_ACTIVITIES', 'SOCIAL_INDIVIDUAL', 'INDIVIDUAL_VISIT'),
  bind(
    'SAH_INDEPENDENCE_SOCIAL_CULTURAL_SUPPORT',
    'SOCIAL_INDIVIDUAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_INDEPENDENCE_SOCIAL_DIGITAL_EDUCATION_SUPPORT',
    'SOCIAL_INDIVIDUAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_INDEPENDENCE_SOCIAL_PERSONAL_AFFAIRS_ASSISTANCE',
    'PERSONAL_AFFAIRS',
    'INDIVIDUAL_VISIT',
    'CASE_ACTIVITY',
  ),
  // Pass-through expense evidence, not worker time.
  bind(
    'SAH_INDEPENDENCE_SOCIAL_PERSONAL_AFFAIRS_EXPENSES',
    'PERSONAL_AFFAIRS_EXPENSE',
    'EXPENSE_EVIDENCE',
  ),

  // --- Therapeutic services -----------------------------------------------
  bind(
    'SAH_INDEPENDENCE_THERAPEUTIC_ACUPUNCTURIST',
    'THERAPEUTIC_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
  ),
  bind('SAH_INDEPENDENCE_THERAPEUTIC_CHIROPRACTOR', 'THERAPEUTIC_PROFESSIONAL', 'INDIVIDUAL_VISIT'),
  bind(
    'SAH_INDEPENDENCE_THERAPEUTIC_DIVERSIONAL_THERAPIST',
    'THERAPEUTIC_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind(
    'SAH_INDEPENDENCE_THERAPEUTIC_REMEDIAL_MASSEUSE',
    'THERAPEUTIC_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
  ),
  bind(
    'SAH_INDEPENDENCE_THERAPEUTIC_ART_THERAPIST',
    'THERAPEUTIC_PROFESSIONAL',
    'INDIVIDUAL_VISIT',
    'GROUP_SESSION',
  ),
  bind('SAH_INDEPENDENCE_THERAPEUTIC_OSTEOPATH', 'THERAPEUTIC_PROFESSIONAL', 'INDIVIDUAL_VISIT'),

  // --- Respite ------------------------------------------------------------
  bind('SAH_INDEPENDENCE_RESPITE_CARE', 'RESPITE', 'INDIVIDUAL_VISIT', 'MULTI_WORKER_VISIT'),

  // --- Transport ----------------------------------------------------------
  bind('SAH_INDEPENDENCE_TRANSPORT_DIRECT', 'TRANSPORT_DIRECT', 'TRIP'),
  // A voucher arrangement is not a worker-kilometre visit.
  bind('SAH_INDEPENDENCE_TRANSPORT_INDIRECT', 'TRANSPORT_INDIRECT', 'VOUCHER_ARRANGEMENT'),

  // --- Assistive technology and home modifications ------------------------
  bind('SAH_AT_HM_ASSISTIVE_TECHNOLOGY', 'AT_HM_LIFECYCLE', 'MILESTONE', 'FULFILMENT'),
  bind('SAH_AT_HM_HOME_MODIFICATIONS', 'AT_HM_LIFECYCLE', 'MILESTONE'),

  // --- Domestic assistance ------------------------------------------------
  bind('SAH_EVERYDAY_LIVING_DOMESTIC_GENERAL_CLEANING', 'DOMESTIC', 'INDIVIDUAL_VISIT'),
  bind('SAH_EVERYDAY_LIVING_DOMESTIC_LAUNDRY', 'DOMESTIC', 'INDIVIDUAL_VISIT'),
  bind('SAH_EVERYDAY_LIVING_DOMESTIC_SHOPPING_ASSISTANCE', 'DOMESTIC', 'INDIVIDUAL_VISIT'),

  // --- Home maintenance and repairs ---------------------------------------
  bind('SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_GARDENING', 'MAINTENANCE_WORK', 'INDIVIDUAL_VISIT'),
  bind('SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_REPAIRS', 'MAINTENANCE_WORK', 'INDIVIDUAL_VISIT'),
  bind('SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_EXPENSES', 'MAINTENANCE_EXPENSE', 'EXPENSE_EVIDENCE'),

  // --- Meals --------------------------------------------------------------
  bind('SAH_EVERYDAY_LIVING_MEALS_PREPARATION', 'MEAL_PREPARATION', 'INDIVIDUAL_VISIT'),
  bind('SAH_EVERYDAY_LIVING_MEALS_DELIVERY', 'MEAL_DELIVERY', 'FULFILMENT'),
];

const BINDING_BY_SERVICE_CODE: ReadonlyMap<string, ServiceExecutionBinding> = new Map(
  SERVICE_EXECUTION_BINDINGS.map((b) => [b.serviceCode, b]),
);

const PUBLISHED_SERVICE_CODES: ReadonlySet<string> = new Set(
  OFFICIAL_SAH_SERVICE_CATALOGUE.map((s) => s.serviceCode),
);

/**
 * Returns `null` for an unknown, retired or unmapped code. Callers must fail
 * closed; there is no generic pack to fall back to.
 *
 * Membership in this module's own binding table is necessary but not
 * sufficient: the official catalogue is the authority on which services exist,
 * so a code must be published there as well. Without that second check a
 * service retired from the catalogue would keep resolving here for as long as
 * its binding row survived, and the worker would proceed on a delisted
 * service. `findRegistryDrift` catches that divergence at publication time;
 * this catches it at the point of use.
 */
export function bindingForServiceCode(
  serviceCode: string | null | undefined,
  publishedServiceCodes: ReadonlySet<string> = PUBLISHED_SERVICE_CODES,
): ServiceExecutionBinding | null {
  if (!serviceCode) return null;
  if (!publishedServiceCodes.has(serviceCode)) return null;
  return BINDING_BY_SERVICE_CODE.get(serviceCode) ?? null;
}

export function isModeAllowedForService(
  serviceCode: string | null | undefined,
  mode: ExecutionMode,
): boolean {
  const binding = bindingForServiceCode(serviceCode);
  return binding ? binding.allowedModes.includes(mode) : false;
}

/**
 * The mode used when an inclusion does not name one. Null for an unknown code.
 */
export function defaultModeForService(
  serviceCode: string | null | undefined,
): ExecutionMode | null {
  return bindingForServiceCode(serviceCode)?.allowedModes[0] ?? null;
}

export function packForServiceCode(serviceCode: string | null | undefined): ServicePackCode | null {
  return bindingForServiceCode(serviceCode)?.basePack ?? null;
}

// ---------------------------------------------------------------------------
// Publication gates
// ---------------------------------------------------------------------------

export interface RegistryDriftIssue {
  serviceCode: string;
  issue: string;
  detail?: string;
}

/**
 * Matrix publication gates 1–4: prove the registry and the official catalogue
 * agree, that every active code has exactly one pack and at least one mode, and
 * that every declared mode can honestly measure that service's unit type.
 *
 * Returns an empty array when the registry is publishable.
 */
export function findRegistryDrift(
  catalogue: readonly {
    serviceCode: string;
    unitType: OfficialSahUnitType;
  }[] = OFFICIAL_SAH_SERVICE_CATALOGUE,
): RegistryDriftIssue[] {
  const issues: RegistryDriftIssue[] = [];
  const catalogueCodes = new Set(catalogue.map((s) => s.serviceCode));

  // Gate 1/4: no binding for a code the catalogue does not publish.
  for (const binding of SERVICE_EXECUTION_BINDINGS) {
    if (!catalogueCodes.has(binding.serviceCode)) {
      issues.push({ serviceCode: binding.serviceCode, issue: 'binding_not_in_catalogue' });
    }
  }

  // Duplicate bindings would silently shadow one another in the map.
  const seen = new Set<string>();
  for (const binding of SERVICE_EXECUTION_BINDINGS) {
    if (seen.has(binding.serviceCode)) {
      issues.push({ serviceCode: binding.serviceCode, issue: 'duplicate_binding' });
    }
    seen.add(binding.serviceCode);
  }

  for (const service of catalogue) {
    const binding = BINDING_BY_SERVICE_CODE.get(service.serviceCode);

    // Gate 1: complete coverage.
    if (!binding) {
      issues.push({ serviceCode: service.serviceCode, issue: 'missing_binding' });
      continue;
    }

    // Gate 2: at least one mode.
    if (binding.allowedModes.length === 0) {
      issues.push({ serviceCode: service.serviceCode, issue: 'no_allowed_mode' });
    }

    // A repeated mode makes "default is first" ambiguous to a reader.
    const uniqueModes = new Set(binding.allowedModes);
    if (uniqueModes.size !== binding.allowedModes.length) {
      issues.push({ serviceCode: service.serviceCode, issue: 'duplicate_allowed_mode' });
    }

    // Gate 3: the mode must be able to measure the catalogue unit.
    for (const mode of binding.allowedModes) {
      if (!isModeCompatibleWithUnitType(mode, service.unitType)) {
        issues.push({
          serviceCode: service.serviceCode,
          issue: 'mode_incompatible_with_unit_type',
          detail: `${mode} cannot measure ${service.unitType}`,
        });
      }
    }
  }

  return issues;
}
